// src/pages/ReadjudicacionMasiva/LoteAuditoria.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Box,
    Paper,
    Grid,
    Button,
    IconButton,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Edit as EditIcon,
    PlayArrow as PlayArrowIcon,
    CheckCircle as CheckCircleIcon,
    Save as SaveIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import useReadjudicacion from '../../hooks/useReadjudicacion';
import { usePermisos } from '../../utils/usePermisos';
import RegistroEditDialog from '../../components/ReadjudicacionMasiva/Auditoria/RegistroEditDialog';
import ValidacionPanel from '../../components/ReadjudicacionMasiva/Auditoria/ValidacionPanel';
import ExpedienteSelector from '../../components/ReadjudicacionMasiva/Auditoria/ExpedienteSelector';

const styles = {
    root: {
        backgroundColor: '#f6f8fc',
        minHeight: '100vh',
        paddingTop: '24px',
        paddingBottom: '24px'
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '24px',
        gap: '16px'
    },
    title: {
        flexGrow: 1,
        fontWeight: 700,
        color: '#5c6bc0'
    },
    paper: {
        padding: '24px',
        marginBottom: '24px'
    },
    tableContainer: {
        maxHeight: 600
    },
    tableRow: {
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
        }
    },
    chipOk: {
        backgroundColor: '#81c784',
        color: '#388e3c',
        fontWeight: 600
    },
    chipError: {
        backgroundColor: '#e57373',
        color: '#d32f2f',
        fontWeight: 600
    },
    chipPendiente: {
        backgroundColor: '#ffb74d',
        color: '#f57c00',
        fontWeight: 600
    },
    chipExcluido: {
        backgroundColor: '#e0e0e0',
        color: '#616161',
        fontWeight: 600
    },
    loadingBox: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 400
    },
    actionsBar: {
        display: 'flex',
        gap: '16px',
        marginTop: '16px',
        justifyContent: 'flex-end'
    }
};

const LoteAuditoria = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { tienePermiso } = usePermisos();

    const {
        loading,
        loteActual,
        registros,
        obtenerLote,
        obtenerRegistrosLote,
        auditarLote,
        editarRegistro,
        finalizarAuditoria,
        excluirRegistro,
        reincluirRegistro
    } = useReadjudicacion();

    const [editDialog, setEditDialog] = useState({ open: false, registro: null });
    const [finalizarDialog, setFinalizarDialog] = useState(false);
    const [expedienteAdm, setExpedienteAdm] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [orderBy, setOrderBy] = useState('Fila');
    const [order, setOrder] = useState('asc');
    const [filtroEstado, setFiltroEstado] = useState(null);

    // Verificar permisos
    useEffect(() => {
        if (!tienePermiso('READJUDICACION MASIVA', 'SELECT')) {
            enqueueSnackbar('No tiene permisos para acceder a esta sección', { variant: 'error' });
            navigate('/gestion-lotes?tab=readjudicacion');
        }
    }, [tienePermiso, navigate, enqueueSnackbar]);

    // Cargar datos del lote
    useEffect(() => {
        if (id) {
            cargarDatos();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const cargarDatos = async () => {
        await Promise.all([
            obtenerLote(id),
            obtenerRegistrosLote(id)
        ]);
    };

    const handleAuditarLote = async () => {
        const success = await auditarLote(id, true);
        if (success) {
            cargarDatos();
        }
    };

    const handleEditarRegistro = (registro) => {
        setEditDialog({ open: true, registro });
    };

    const handleSaveRegistro = async (idRegistro, datos) => {
        const success = await editarRegistro(idRegistro, datos);
        if (success) {
            setEditDialog({ open: false, registro: null });
            cargarDatos();
        }
    };

    const handleExcluirRegistro = async (idRegistro) => {
        const success = await excluirRegistro(idRegistro);
        if (success) {
            setEditDialog({ open: false, registro: null });
            cargarDatos();
        }
    };

    const handleReincluirRegistro = async (idRegistro) => {
        const success = await reincluirRegistro(idRegistro);
        if (success) {
            setEditDialog({ open: false, registro: null });
            cargarDatos();
        }
    };

    const handleFinalizarAuditoria = async () => {
        if (!expedienteAdm) {
            enqueueSnackbar('Debe seleccionar un expediente administrativo', { variant: 'warning' });
            return;
        }

        // Verificar que no hay registros defectuosos o pendientes
        const hayErrores = registros.some(r => r.Ok === 0 || r.Ok === 2);
        if (hayErrores) {
            enqueueSnackbar('No se puede finalizar: hay registros con errores o pendientes', { variant: 'error' });
            return;
        }

        const success = await finalizarAuditoria(id, expedienteAdm.Id);
        if (success) {
            enqueueSnackbar('Auditoría finalizada. Puede continuar con el desapoderamiento', { variant: 'success' });
            setFinalizarDialog(false);
            navigate('/gestion-lotes?tab=readjudicacion');
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const getEstadoChip = (ok) => {
        switch (ok) {
            case 1:
                return <Chip label="OK" size="small" sx={styles.chipOk} />;
            case 0:
                return <Chip label="Error" size="small" sx={styles.chipError} />;
            case 2:
                return <Chip label="Pendiente" size="small" sx={styles.chipPendiente} />;
            case 3:
                return <Chip label="Excluido" size="small" sx={styles.chipExcluido} />;
            default:
                return <Chip label="Desconocido" size="small" />;
        }
    };

    // Filtrar por estado si hay filtro activo
    const registrosFiltrados = filtroEstado !== null
        ? registros.filter(r => r.Ok === filtroEstado)
        : registros;

    const sortedRegistros = [...registrosFiltrados].sort((a, b) => {
        let aValue = a[orderBy];
        let bValue = b[orderBy];

        // Manejo especial para campos numéricos
        if (orderBy === 'Fila' || orderBy === 'Ok' || orderBy === 'Campo4') {
            aValue = parseFloat(aValue) || 0;
            bValue = parseFloat(bValue) || 0;
        }

        // Comparación
        if (order === 'asc') {
            return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        } else {
            return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
        }
    });

    const registrosPaginados = sortedRegistros.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    if (loading && !loteActual) {
        return (
            <Container maxWidth={false} sx={styles.root}>
                <Box sx={styles.loadingBox}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth={false} sx={styles.root}>
            {/* Header */}
            <Box sx={styles.header}>
                <IconButton
                    onClick={() => navigate('/gestion-lotes?tab=readjudicacion')}
                    style={{ color: '#5c6bc0' }}
                    size="large">
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" style={{ fontWeight: 700, color: '#5c6bc0', flex: 1 }}>
                    Auditoría de Lote {id}
                </Typography>
                <Tooltip title="Refrescar">
                    <IconButton
                        onClick={cargarDatos}
                        disabled={loading}
                        style={{ color: '#5c6bc0' }}
                        size="large">
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>
            {/* Panel de resumen - Ahora arriba con filtros */}
            <ValidacionPanel
                registros={registros}
                filtroActivo={filtroEstado}
                onFiltroChange={setFiltroEstado}
            />
            <Grid container spacing={3}>
                {/* Panel principal - Tabla de registros (ancho completo) */}
                <Grid size={12}>
                    <Paper sx={styles.paper}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
                            <Typography variant="h6">
                                Registros del Lote
                            </Typography>
                            {tienePermiso('READJUDICACION MASIVA', 'UPDATE') && (
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<PlayArrowIcon />}
                                    onClick={handleAuditarLote}
                                    disabled={loading}
                                >
                                    Auditar Lote
                                </Button>
                            )}
                        </Box>

                        <TableContainer sx={styles.tableContainer}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            style={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => handleRequestSort('Fila')}
                                        >
                                            Fila {orderBy === 'Fila' && (order === 'asc' ? '↑' : '↓')}
                                        </TableCell>
                                        <TableCell
                                            style={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => handleRequestSort('Ok')}
                                        >
                                            Estado {orderBy === 'Ok' && (order === 'asc' ? '↑' : '↓')}
                                        </TableCell>
                                        <TableCell
                                            style={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => handleRequestSort('Campo1')}
                                        >
                                            Nombre {orderBy === 'Campo1' && (order === 'asc' ? '↑' : '↓')}
                                        </TableCell>
                                        <TableCell
                                            style={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => handleRequestSort('Campo2')}
                                        >
                                            DNI/CUIT {orderBy === 'Campo2' && (order === 'asc' ? '↑' : '↓')}
                                        </TableCell>
                                        <TableCell
                                            style={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => handleRequestSort('Campo3')}
                                        >
                                            Certificado {orderBy === 'Campo3' && (order === 'asc' ? '↑' : '↓')}
                                        </TableCell>
                                        <TableCell
                                            style={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => handleRequestSort('Campo4')}
                                        >
                                            Monto {orderBy === 'Campo4' && (order === 'asc' ? '↑' : '↓')}
                                        </TableCell>
                                        <TableCell
                                            style={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => handleRequestSort('Campo7')}
                                        >
                                            Domicilio {orderBy === 'Campo7' && (order === 'asc' ? '↑' : '↓')}
                                        </TableCell>
                                        <TableCell
                                            style={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => handleRequestSort('Obs')}
                                        >
                                            Observaciones {orderBy === 'Obs' && (order === 'asc' ? '↑' : '↓')}
                                        </TableCell>
                                        <TableCell align="center" style={{ fontWeight: 600 }}>Acciones</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {registrosPaginados.map((registro, idx) => (
                                        <TableRow key={registro.Id} sx={{ ...styles.tableRow, backgroundColor: idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}>
                                            <TableCell>{registro.Fila}</TableCell>
                                            <TableCell>{getEstadoChip(registro.Ok)}</TableCell>
                                            <TableCell>{registro.Campo1}</TableCell>
                                            <TableCell>{registro.Campo2}</TableCell>
                                            <TableCell>{registro.Campo3}</TableCell>
                                            <TableCell>
                                                {registro.Campo4 ? `$${parseFloat(registro.Campo4).toFixed(2)}` : '-'}
                                            </TableCell>
                                            <TableCell>{registro.Campo7}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" noWrap style={{ maxWidth: 200 }}>
                                                    {registro.Obs || '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                {tienePermiso('READJUDICACION MASIVA', 'UPDATE') && (
                                                    <Tooltip title="Editar">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleEditarRegistro(registro)}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            component="div"
                            count={sortedRegistros.length}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            labelRowsPerPage="Filas por página:"
                            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}${filtroEstado !== null ? ' (filtrados)' : ''}`}
                        />

                        {/* Barra de acciones */}
                        {tienePermiso('READJUDICACION MASIVA', 'UPDATE') && loteActual?.Estado === 0 && (
                            <Box sx={styles.actionsBar}>
                                <ExpedienteSelector
                                    value={expedienteAdm}
                                    onChange={setExpedienteAdm}
                                    disabled={loading}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<CheckCircleIcon />}
                                    onClick={() => setFinalizarDialog(true)}
                                    disabled={loading || !expedienteAdm}
                                >
                                    Finalizar Auditoría
                                </Button>
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>
            {/* Dialog - Editar Registro */}
            <RegistroEditDialog
                open={editDialog.open}
                registro={editDialog.registro}
                onClose={() => setEditDialog({ open: false, registro: null })}
                onSave={handleSaveRegistro}
                onExcluir={handleExcluirRegistro}
                onReincluir={handleReincluirRegistro}
            />
            {/* Dialog - Confirmar Finalización */}
            <Dialog open={finalizarDialog} onClose={() => setFinalizarDialog(false)}>
                <DialogTitle>Finalizar Auditoría</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        ¿Está seguro que desea finalizar la auditoría del lote {id}?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Esta acción cambiará el estado del lote y permitirá continuar con el desapoderamiento de abogados.
                    </Typography>
                    {expedienteAdm && (
                        <Box marginTop={2}>
                            <Typography variant="body2">
                                <strong>Expediente Administrativo:</strong>
                            </Typography>
                            <Typography variant="body2">
                                {expedienteAdm.Expediente} - {expedienteAdm.Caratula}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFinalizarDialog(false)}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleFinalizarAuditoria}
                        color="primary"
                        variant="contained"
                        startIcon={<SaveIcon />}
                    >
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default LoteAuditoria;
